import { inject, Inject, Injectable, NgZone, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { throwError, BehaviorSubject, of, Subject, Observable } from 'rxjs';

import { User } from './user/user.model';
import { UserBase } from './user/user-base.interface';
import { Guest } from './user/guest.model';
import { UserProfile } from './user/user-profile.model';
import { UserRole } from './user/user-role.model';
import { ApplicationService, UserExtraLite, UserExtraLiteAdmin } from '@frontend/common';
import { PlaceholderService } from '../utilities/placeholder.service';
import { LoadingService, LoadingObject } from '../loaders';
import { APP_ENV, EnvironmentModuleConfig } from '@frontend/environment';
import { LoginReturnTargetService } from './login-return-target.service';

export interface AuthResponseData {
  id: number;
  name: string;
  fname: string;
  lname: string;
  pre_honorific: string;
  post_honorific: string;
  email: string;
  email_verified_at: string;
  created_at: string;
  password_set: boolean;

}

interface RegistrationResponseData {

  created_at: string; // 2022-12-03 21:04:32;
  deleted_at: string; //: null;
  email: string; // richard@richardfarkas.net;
  email_pending: boolean; // null;
  email_verified_at: string; // null;
  fname: string; // Richard;
  id: number; // 7;
  lname: string; // Farkas;
  name: string; // null;
  avatar_disk: string; // null;
  avatar_path: string; // null;
  post_honorific: string; // null;
  pre_honorific: string; // null;
  slug: string; // richard-farkas;
  updated_at: string; // 2022-12-03 21:04:32;

}

interface UserResponseData {
  data: {
    created_at: string; // "2021-03-13 21:27:51"
    email: string; // "tester104@poweroflearning.net"
    email_verified_at: string; // "2021-03-13 21:31:59"
    email_pending: string; // "myalternative@anotheremail.com"
    fname: string; // "Tester104"
    pre_honorific: string; // Dr
    post_honorific: string; // PhD
    name_full_honorific: string; // Dr Tester104 Registering PhD
    id: number; // 50
    lname: string; // "Registering"
    slug: string; // "rich_international"
    avatar_disk: string; // e.g. 'cloudinary'
    avatar_path: string; // e.g. 'users/avatars/xyz.jpg'
    profile: UserProfile;
    roles: UserRole[];
    updated_at: string; // "2021-03-13 21:31:59",
    password_set: boolean;
  };
}

interface TokenResponse {
  token: string;
}

export type UpdateUserRequestBody = {
  fname?: string;
  lname?: string;
  pre_honorific?: string | null;
  post_honorific?: string | null;
};
interface UpdateUserResponseUser extends UserBase {
  created_at: string;
}
interface UpdateUserSuccessResponse {
  data: UpdateUserResponseUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  user = new BehaviorSubject<User | null | undefined>(undefined);
  guest = new BehaviorSubject<Guest | null >(null);
  private _promptAuthentication = new Subject<number>();
  public promptAuthentication : Observable<number>;
  _token: string | null = null;
  verificationSuccess = null;
  // apiUrl : string = 'https://cebt.fi'; // temporary solution until we can resolve the circular dependency error between AuthService and environment variables in the Core library
  private _loadingObject = signal<LoadingObject>({});  // In the legacy application, loadingService caused a circular dependency error.
  private loadingService = inject(LoadingService);
  private loginReturnTargetService = inject(LoginReturnTargetService);


    get loadingObject(): { [key: string]: boolean } {
        return this._loadingObject();
    }
    public get loadingObjectSignal() : WritableSignal<LoadingObject> {
        return this._loadingObject;
    }
    isAnythingLoading(): boolean {
        return this.loadingService.isAnythingLoading(this._loadingObject());
    }

    setLoadingStatus(key: string, status: boolean): void {
        this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
    }

  constructor(
    private http: HttpClient,
    private router: Router,
    private zone: NgZone,
    private applicationService : ApplicationService,
    private placeholderService : PlaceholderService,
    @Inject(APP_ENV) private appEnvironment: EnvironmentModuleConfig,
  ) {
    window['handleLinkedInCallback'] = (code, state) => {
      this.zone.run(() => {
        this.completeSocialOauth(code, state).subscribe(
          () => {
            // this.loading = false;
          },
          (errorMessage) => {
            // TODO - add error handling
            /* this.error = errorMessage;
                this.loading = false; */
          }
        );
      });
    };
    this.getSavedGuest();
    this._promptAuthentication = new Subject<number>();
    this.promptAuthentication = this._promptAuthentication.asObservable();
  }

  getSavedGuest(){
    if(localStorage.getItem('guest')){
      this.guest.next(JSON.parse(localStorage.getItem('guest')));
    }
  }

  getFrontendUrl (){
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }

    // Keeps protocol + host + port in local dev and production.
    const { origin, protocol, host } = window.location;
    const baseUrl = origin && origin !== 'null' ? origin : `${protocol}//${host}`;
    return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  }

  register(email: string, password: string, fname: string, lname: string) {
    return this.http
      .post<RegistrationResponseData>('api/v1/register', {
        email: email,
        password: password,
        fname: fname,
        lname: lname,
        frontend_url: this.getFrontendUrl(),
      })
      .pipe(
        tap((responseData) => {
          // TODO - some better user experience than nothing?
        }),
        catchError((error)=>this.handleError(error)),
      );
  }

  get token() {
    return this._token || localStorage.getItem('token');
  }

  saveToken(tokenObject: TokenResponse) {
    this._token = tokenObject.token;
    localStorage.setItem('token', this._token);
  }

  login(email: string, password: string) {
    return this.http.get("sanctum/csrf-cookie", {responseType: 'text'})
      .pipe(
        switchMap(() => {
          return this.http.post<TokenResponse>('api/v1/frontend/login', {
            email: email,
            password: password,
          })
        }),
        tap((responseData) => this.saveToken(responseData)),
        catchError((error)=>this.handleError(error)),
        switchMap(() => this.getUser(null)),
        map(() => undefined),
      );
  }
  changeEmail(newEmail: string) {
    return this.http
      .post<any>('api/v1/user/change-email', {
        frontend_url: this.getFrontendUrl(),
        email: newEmail,
      })
      .pipe(
        map((responseData) => {
          let userProfile;
          if (responseData.data.profile) {
            userProfile = this.transformProfileResponse(
              responseData.data.profile
            );
          }
          let user : User = this.transformUserResponse(responseData.data, userProfile);
          this.user.next(user);
          localStorage.setItem('user', JSON.stringify(user));
          return true;
        }),
        catchError((error)=>this.handleError(error)),
      );
  }
  changePassword(oldPassword: string, newPassword: string) {
    return this.http
      .post<any>('api/v1/change-password', {
        old_password: oldPassword,
        password: newPassword,
      })
      .pipe(
        map((responseData) => {
          let user = this.user.getValue();
          if (user && !user.password_set) {
            const updatedUser = { ...user, password_set: true };
            this.user.next(updatedUser);
          }
        }),
        catchError((error)=>this.handleError(error)),
      );
  }
  forgotPassword(email: string) {
    let testing = this.getFrontendUrl();
    console.log('testing '+ testing);
    return this.http
      .post<any>('api/v1/forgot-password', {
        email: email,
        frontend_url: this.getFrontendUrl(),
      })
      .pipe(
        map((responseData) => {
          // TODO - review and remove?
        }),
        catchError((error)=>this.handleError(error)),

      );
  }
  resetPassword(
    email: string,
    password: string,
    password_confirmation: string,
    token: string
  ) {
    return this.http
      .post<any>('api/v1/reset-password', {
        password: password,
        password_confirmation: password_confirmation,
        email: email,
        token: token,
      })
      .pipe(
        map((responseData) => {
          // TODO - review and remove?
        }),
        catchError(error=>this.handleError(error))
      );
  }
  loginWithLinkedin() {
    const apiUrl = this.appEnvironment.apiUrl;
    window.open(
      apiUrl + '/auth/linkedin/redirect?site=' + this.getFrontendUrl(),
      'cebtlinkedin',
      'resizable=no, toolbar=no, scrollbars=no, menubar=no, status=no, directories=no, location=no, width=600, height=600, left=100 top=100'
    );
  }

  completeSocialOauth(code: string, state: string) {
    let provider = 'linkedin';
    return this.http
      .get<TokenResponse>(
        'api/v1/signin-step-two-callback-url-' +
          provider +
          '?code=' +
          code +
          '&state=' +
          state
      )
      .pipe(
        tap((responseData) => this.saveToken(responseData)),
        catchError(error=>this.handleError(error)),
        switchMap(() => this.getUser(false)),
        tap(() => this.navigateAfterAuthentication()),
      );
  }

  removeCachedUser() {
    this._token = null;
    this.user.next(undefined);
  }

  verify(url) {
    return this.http.get(url).pipe(catchError( error => this.handleError(error)));
  }
  mergeUserProfileIntoUserExtraLite(userLiteResponse): UserExtraLite {
    return new UserExtraLite(
      userLiteResponse.id,
      userLiteResponse.fname,
      userLiteResponse.lname,
      userLiteResponse.pre_honorific,
      userLiteResponse.post_honorific,
      userLiteResponse.name_full_honorific,
      userLiteResponse.avatar_disk,
      userLiteResponse.avatar_path,
      userLiteResponse.slug,
      userLiteResponse.profile.job_title,
      userLiteResponse.profile.summary,
      userLiteResponse.profile.bio
    );
  }
  mergeUserProfileIntoUserExtraLiteAdmin(userLiteResponse): UserExtraLiteAdmin {
    return new UserExtraLiteAdmin(
      userLiteResponse.id,
      userLiteResponse.email,
      userLiteResponse.fname,
      userLiteResponse.lname,
      userLiteResponse.pre_honorific,
      userLiteResponse.post_honorific,
      userLiteResponse.name_full_honorific,
      userLiteResponse.avatar_disk,
      userLiteResponse.avatar_path,
      userLiteResponse.slug,
      userLiteResponse.profile.job_title,
      userLiteResponse.profile.summary,
      userLiteResponse.profile.bio
    );
  }
  transformProfileResponse(profileResponse): UserProfile {
    return new UserProfile(
      profileResponse.id,
      profileResponse.job_title,
      profileResponse.summary,
      profileResponse.bio,
      new Date(profileResponse.created_at),
      new Date(profileResponse.updated_at),
      profileResponse.user_id
    );
  }
  transformUserResponse(userResponse : UserResponseData['data'], transformedProfile: UserProfile): User {
    const createdAt : Date = userResponse.created_at      ? new Date(userResponse.created_at)
      : this.getUserProperty('created_at')
        ? new Date(this.getUserProperty('created_at'))
        : new Date();
    return {...userResponse, profile : transformedProfile, created_at: createdAt} ;
  }

  getUser(fullEditable: boolean) {
    let url = 'api/v1/get-user';
    if (fullEditable) {
      url += '?mode=edit';
    }
    return this.http.get(url).pipe(
      tap((responseData: UserResponseData) => {
        if (!responseData || !responseData.data) {
          throw 'not the expected response';
        }
        let userProfile;
        if (responseData.data.profile) {
          userProfile = this.transformProfileResponse(
            responseData.data.profile
          );
        }
        let user : User = this.transformUserResponse(responseData.data, userProfile);
        this.user.next(user);
        localStorage.setItem('user', JSON.stringify(user));
      }),
      catchError(error=>{
        this.user.next(null);
        return this.handleError(error)
      }),
    );
  }

  updateUser(requestBody: UpdateUserRequestBody = {}) {

    const payload: UpdateUserRequestBody = { ...requestBody };
    if (payload.pre_honorific === '') {
      payload.pre_honorific = null;
    }
    if (payload.post_honorific === '') {
      payload.post_honorific = null;
    }

    this.setLoadingStatus('user', true);

    return this.http.patch<UpdateUserSuccessResponse>('api/v1/user', payload).pipe(
      map((response) => {
        if (!response?.data) {
          return null;
        }

        const userFromStore = this.user.getValue();
        if (!userFromStore) {
          return response.data;
        }

        const createdAt : Date = response.data.created_at      ? new Date(response.data.created_at)
          : this.getUserProperty('created_at')
            ? new Date(this.getUserProperty('created_at'))
            : new Date();

        const updatedUser : User = {...userFromStore, ...response.data, created_at: createdAt};

        this.user.next(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return response.data;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 422 && error?.error?.errors) {
          return throwError(error.error);
        }
        return this.handleError(error);
      }),
      finalize(() => {
        this.setLoadingStatus('user', false);
      }),
    );
  }

  /**
   * @deprecated Use updateUser instead.
   */
  updateProfileField(fieldKey: string, fieldValue: string) {
    return this.updateUser({ [fieldKey]: fieldValue ? fieldValue : null });
  }
  public getMatchingUserRoles (roles : UserRole[],arrayOfRoleTitles : string[]) : UserRole[] {
      let filteredRoles = [];
      if(roles?.length && arrayOfRoleTitles?.length){
        arrayOfRoleTitles.forEach(t => {
          let role = roles.find(r=>r.title === t);
          if (role){
            filteredRoles.push(role);
          }
        });
      }
      return filteredRoles;
  }

  setVerificationSuccess(bool) {
    this.verificationSuccess = bool;
  }
  getVerificationSuccess() {
    return this.verificationSuccess;
  }
  /**
   * Remembers where to take the user after they log in or register.
   * @param url an application URL such as '/tips/some-slug?tab=2#comments', or null to forget it
   */
  setRouteForRedirectAfterAuth(url: string | null) {
    if (url) {
      this.loginReturnTargetService.remember(url);
    } else {
      this.loginReturnTargetService.clear();
    }
  }
  /** Remembers the current page, with its query and fragment, as where to return after login */
  setCurrentRouteForRedirectAfterAuth(url: string = this.router.url): void {
    this.setRouteForRedirectAfterAuth(url);
  }
  openLoginModal(routeForRedirectAfterAuth : string | null = null){
    if(routeForRedirectAfterAuth){
      this.setRouteForRedirectAfterAuth(routeForRedirectAfterAuth);
    }
    let value = Math.random()*100000;
    this._promptAuthentication.next(value);
  }
  getRouteForRedirectAfterAuth(): string | null {
    return this.loginReturnTargetService.peek();
  }
  /** After a successful login: go to the remembered page, once, or else to the fallback */
  navigateAfterAuthentication(fallbackUrl = '/'): Promise<boolean> {
    return this.router.navigateByUrl(this.loginReturnTargetService.take() ?? fallbackUrl);
  }
  checkRole(whichRole) {
    if (!this.user.getValue() || !this.user.getValue().roles) {
      return false;
    }
    let roles = this.user.getValue().roles;
    return Boolean(roles.find((r) => r.title === whichRole));
  }
  getUserProperty(propertyKey: string) {
    if (!this.user.getValue() || !this.user.getValue()[propertyKey]) {
      return null;
    }
    return this.user.getValue()[propertyKey];
  }
  makeGuest(guest_uuid: string,event_uuid: string,cohort_uuid: string){
    let subdomain = this.applicationService.getSubdomain();
    let url = 'api/v1/make-guest?subdomain='+subdomain+'&';
    if (event_uuid) {
      url += 'event='+event_uuid+'&';
    };
    if (cohort_uuid) {
      url += 'event='+cohort_uuid+'&';
    };
    return this.http.get<{data:Guest}>(url).pipe(
      catchError(error=>this.handleError(error)),
      map((responseData) => {
        let guest : Guest = responseData.data;
        localStorage.setItem('guest', JSON.stringify(guest));
        this.guest.next(guest);
      })
    );

  }
  importGuest(guest_uuid: string){
    if(!guest_uuid){this.handleError(new HttpErrorResponse({statusText:'not done'}))};
    return this.http.get<{data:any}>('api/v1/import-guest/'+guest_uuid).pipe(
      catchError(error=>{
        if(error?.status === 404 && error.error){
          return of({data: {success : true}});
        }
        return this.handleError(error);
      }),
      map((responseData) => {
        let response = responseData.data;
        this.removeGuestLocally();
        return response;
      })
    );
  }
  removeGuest(guest_uuid: string){
    if(!guest_uuid){this.handleError(new HttpErrorResponse({statusText:'not done'}))};
    return this.http.get<{data:any}>('api/v1/remove-guest/'+guest_uuid).pipe(
      catchError(error=>{
        if(error?.status === 404 && error.error){
          return of({data: {success : true}});
        }
        return this.handleError(error);
      }),
      map((responseData) => {
        let response = responseData.data;
        this.removeGuestLocally();
        return response;
      })
    );
  }
  removeGuestLocally(){
    localStorage.removeItem('guest');
    this.guest.next(null);
  }
  getDummyUsersExtraLite(count : number = 1) : UserExtraLite[]{
    let randomNames = ['Robin','Jo','Jay','Leslie',];
    let job_titles = ['Engineer','Teacher','Consultant','Care worker','Administrator',];
    let text = 'Lorem ipsum dolor sit amet consectetur adipisicing elit. Blanditiis, dolor';
    let users = [];
    Array.from({ length: count }, (_, index) => index).forEach(element => {
      let fname = randomNames[Math.ceil(Math.random()*randomNames.length-1)];
      let lname = randomNames[Math.ceil(Math.random()*randomNames.length-1)];
      let job_title = job_titles[Math.ceil(Math.random()*job_titles.length-1)];
      users.push(new UserExtraLite(null,fname,lname,null,null,fname+' '+lname,'external',this.placeholderService.makeAvatarFile(),null,job_title,text,text+'. '+text))
    });
    return users;
  }
  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'error.something_went_wrong';
    if (!errorResponse.error || !errorResponse.error.message) {
      return throwError(errorMessage);
    }
    if (
      errorResponse.error.errors &&
      errorResponse.error.errors.email &&
      errorResponse.error.errors.email[0]
    ) {
      errorResponse.error.errors.email[0] = errorResponse.error.errors.email[0].replace(
        "'",
        ''
      ); // remove the hyphens ... We cant find a user with that e-mail address.
      switch (errorResponse.error.errors.email[0]) {
        case 'The provided credentials are incorrect.':
          errorMessage = 'authentication.login_credentials_wrong';
          break;
        case 'The email has already been taken.':
          errorMessage = 'authentication.already_registered_log_in';
          break;
        case 'The email field must be a valid email address.':
          errorMessage = 'authentication.invalid_address';
          break;
        case 'The email must be a valid email address.':
          errorMessage = 'authentication.invalid_address';
          break;
        case 'This password reset token is invalid.':
          errorMessage = 'authentication.password_reset_invalid_link';
          break;
        case 'We cant find a user with that e-mail address.':
          errorMessage = 'authentication.forgot_password_not_found';
          break;
      }
    } else if (
      errorResponse.error.errors &&
      errorResponse.error.errors.password
    ) {
      const passwordError: string = errorResponse.error.errors.password[0];
      if (passwordError === 'password_compromised') {
        errorMessage = 'authentication.password_compromised';
      } else if (passwordError.includes('must be at least')) {
        errorMessage = 'authentication.password_min_length';
      } else if (passwordError.includes('greater than')) {
        errorMessage = 'common.too_long';
      } else if (passwordError.includes('confirmation does not match')) {
        errorMessage = 'authentication.password_confirmation_error';
      }
    } else if (errorResponse.error.message == 'password_incorrect') {
      errorMessage = 'authentication.password_wrong';
    } else if (errorResponse.error.message == 'Too many guests.'){
      errorMessage = 'authentication.guest_quota_full';
    }
    if (errorResponse.error.meta){
      return throwError({message:errorMessage,meta:errorResponse.error.meta});
    }
    return throwError(errorMessage);
  }
}
